import {Suspense, useEffect, useState} from 'react';
import {useNavigate} from 'react-router';
import {diApi} from '@di-shared/api/di';
import {CreateFinancialAgreementRequest} from '@di-shared/api/di/__generated';
import {PermissionTypeKr} from '@di-shared/api/di/type';
import {emitEventLog} from '@di-shared/features/analytics';
import useCapitalTerms from '@di-shared/features/capital/useCapitalTerms';
import useApiError from '@di-shared/features/common/useApiError';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import environmentStore from '@di-shared/features/environment/environmentStore';
import useFirebaseStore from '@di-shared/features/environment/firebaseStore';
import useAvailableComponentKr from '@di-shared/features/home/kr/useAvailableComponent.kr';
import {getEvent2Url} from '@di-shared/pages/[vin]/capital/event1';
import {ROUTE_PATHS} from '@di-shared/routes';
import {getQueryKey} from '@shared/queryKeyMap';
import usePendingHandler from '@shared/usePendingHandler';
import {WebviewUtils} from '@shared/webviewUtils';
import {useQueryClient} from '@tanstack/react-query';

export interface CapitalTermsProps {
  vin: string;
}

interface CapitalTermsSessionState {
  selectedTermsIds?: string[];
  isThirdPartyChecked?: boolean;
  isMobilityConsumptionChecked?: boolean;
}

function getCapitalTermsSessionStorageKey(vin: string) {
  return `capital-terms:${vin}`;
}

function getCapitalTermsSessionState(vin: string): CapitalTermsSessionState {
  const savedState = sessionStorage.getItem(getCapitalTermsSessionStorageKey(vin));

  if (!savedState) {
    return {};
  }

  return JSON.parse(savedState) as CapitalTermsSessionState;
}

function CapitalTerms({vin}: CapitalTermsProps) {
  return (
    <Suspense>
      <Inner vin={vin} />
    </Suspense>
  );
}

function Inner({vin}: CapitalTermsProps) {
  const queryClient = useQueryClient();
  const puid = environmentStore.getState().puid;
  const {t} = useInferableTranslation();
  const navigate = useNavigate();
  const {resolveError} = useApiError();
  const {backOrClose, openAlert} = useCcWebviewUtilsHelper();
  const {getFeatureAgreed} = useAvailableComponentKr({vin});
  const {financialTerms} = useCapitalTerms();
  const {resolveFeatureFlag} = useFirebaseStore((state) => ({resolveFeatureFlag: state.resolveFeatureFlag}));

  const sessionStorageKey = getCapitalTermsSessionStorageKey(vin);
  const savedConsentState = getCapitalTermsSessionState(vin);

  const isFinanceManageAgree = getFeatureAgreed(PermissionTypeKr.FINANCIAL_MANAGEMENT);
  const isFinanceManageAgree3rd = getFeatureAgreed(PermissionTypeKr.FINANCIAL_MANAGEMENT_3RD);
  const isMobilityConsumptionVisible = !!resolveFeatureFlag('KR_MOBILITY_CONSUMPTION', vin);
  const isMobilityConsumptionAgree = getFeatureAgreed(PermissionTypeKr.MOBILITY_CONSUMPTION);
  const [selectedTermsIds, setSelectedTermsIds] = useState<string[]>(() => savedConsentState.selectedTermsIds ?? []);
  const [isThirdPartyChecked, setIsThirdPartyChecked] = useState(() => savedConsentState.isThirdPartyChecked ?? false);
  const [isMobilityConsumptionChecked, setIsMobilityConsumptionChecked] = useState(() => savedConsentState.isMobilityConsumptionChecked ?? false);
  // 모빌리티 소비 동의는 선택 동의라 다른 동의 항목이 남아있으면 체크 안해도 버튼 활성화됨.
  // 모빌리티 소비 동의만 혼자 남는 경우엔 선택해야만 버튼 활성화됨.
  const otherRequiredConsentCount = (isFinanceManageAgree ? 0 : financialTerms.length) + (isFinanceManageAgree3rd ? 0 : 1);
  const isMobilityConsumptionOnlyRemaining = isMobilityConsumptionVisible && otherRequiredConsentCount === 0 && !isMobilityConsumptionAgree;
  const requiredConsentCount = otherRequiredConsentCount + (isMobilityConsumptionOnlyRemaining ? 1 : 0);
  const selectedConsentCount =
    selectedTermsIds.length + (isThirdPartyChecked ? 1 : 0) + (isMobilityConsumptionOnlyRemaining && isMobilityConsumptionChecked ? 1 : 0);

  useEffect(() => {
    sessionStorage.setItem(
      sessionStorageKey,
      JSON.stringify({
        selectedTermsIds,
        isThirdPartyChecked,
        isMobilityConsumptionChecked,
      } satisfies CapitalTermsSessionState)
    );
  }, [isMobilityConsumptionChecked, isThirdPartyChecked, selectedTermsIds, sessionStorageKey]);

  const [agreeFinanceManagement] = usePendingHandler(
    async () => {
      WebviewUtils.loading(true);
      await diApi.financialManagementController.createFinancialAgreement({
        requestBody: {
          puid,
          vin,
          featureNames: [
            ...(isFinanceManageAgree ? ['FINANCIAL_MANAGEMENT_3RD'] : ['FINANCIAL_MANAGEMENT', 'FINANCIAL_MANAGEMENT_3RD']),
            ...(isMobilityConsumptionChecked ? ['MOBILITY_CONSUMPTION'] : []),
          ] as CreateFinancialAgreementRequest['featureNames'],
        },
      });
      await queryClient.invalidateQueries({queryKey: [getQueryKey(diApi.vehicle.getVehicle)]});
      sessionStorage.removeItem(sessionStorageKey);
      WebviewUtils.loading(false);

      // [2025-08-21:cy.seo] FIXME: 캐피탈 이벤트 특별 동작 - 이벤트 종료 후 제거할것 EVENT2
      if (window.location.search && window.location.search.includes('from=event')) {
        const nextScheme = `app://webView?isModalEntry=true&showNavigationBar=false&showToolbar=false&url=${encodeURIComponent(getEvent2Url(vin))}`;
        WebviewUtils.close(nextScheme);
        return;
      }
      if (isMobilityConsumptionOnlyRemaining) {
        backOrClose();
        return;
      }
      navigate(ROUTE_PATHS.HOME_FINANCE_TAB.getFullPath(vin), {replace: true});
    },
    async (error) => {
      WebviewUtils.loading(false);
      const [execute, code] = await resolveError(error);
      if (code === 40315) {
        await openAlert({title: t('T30648'), message: t('T30649')});
        return;
      }
      execute();
    }
  );

  function updateTerms(termsId: string) {
    setSelectedTermsIds((prev) => (prev.includes(termsId) ? prev.filter((id) => id !== termsId) : [...prev, termsId]));
  }

  function openTermsDetail(termsId: string) {
    const nextSelectedTermsIds = selectedTermsIds.includes(termsId) ? selectedTermsIds : [...selectedTermsIds, termsId];
    setSelectedTermsIds(nextSelectedTermsIds);
    sessionStorage.setItem(
      sessionStorageKey,
      JSON.stringify({
        selectedTermsIds: nextSelectedTermsIds,
        isThirdPartyChecked,
        isMobilityConsumptionChecked,
      } satisfies CapitalTermsSessionState)
    );
    navigate(ROUTE_PATHS.CAPITAL_TERMS_DETAIL.getFullPath(vin, termsId));
  }

  function openThirdPartyTermsDetail() {
    setIsThirdPartyChecked(true);
    sessionStorage.setItem(
      sessionStorageKey,
      JSON.stringify({
        selectedTermsIds,
        isThirdPartyChecked: true,
        isMobilityConsumptionChecked,
      } satisfies CapitalTermsSessionState)
    );
    navigate(ROUTE_PATHS.CAPITAL_TERMS_3RD_DETAIL.getFullPath(vin));
  }

  function openMobilityConsumptionTermsDetail() {
    setIsMobilityConsumptionChecked(true);
    sessionStorage.setItem(
      sessionStorageKey,
      JSON.stringify({
        selectedTermsIds,
        isThirdPartyChecked,
        isMobilityConsumptionChecked: true,
      } satisfies CapitalTermsSessionState)
    );
    navigate(ROUTE_PATHS.CAPITAL_TERMS_ACCOUNT_BOOK_DETAIL.getFullPath(vin));
  }

  return (
    <>
      <main className="content page_eco_mileage">
        <section className="section section_mileage_consent">
          <div className="section_heading">
            <h2
              className="section_heading_title"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {isFinanceManageAgree ? t('T40159') : t('T40160')}
            </h2>
            {isFinanceManageAgree && <p className="section_heading_desc">{t('T40152')}</p>}
          </div>

          <div className="consent_agree">
            <div className="divider"></div>
            {!isFinanceManageAgree &&
              financialTerms.map((terms) => (
                <div
                  key={terms.termsId}
                  className="consent_agree_item"
                >
                  <div className="input_box">
                    <input
                      type="checkbox"
                      id={`checkbox_${terms.termsId}`}
                      className="input"
                      checked={selectedTermsIds.includes(terms.termsId)}
                      onChange={() => updateTerms(terms.termsId)}
                    />
                    <label
                      htmlFor={`checkbox_${terms.termsId}`}
                      className="label"
                    >
                      {terms.title}
                    </label>
                  </div>
                  <button
                    type="button"
                    className="btn btn_ic"
                    onClick={() => openTermsDetail(terms.termsId)}
                  >
                    <span className="ic ic_arrow_forward_16px">
                      <span className="blind">{t('T10063')}</span>
                    </span>
                  </button>
                </div>
              ))}
            {!isFinanceManageAgree3rd && (
              <div className="consent_agree_item">
                <div className="input_box">
                  <input
                    type="checkbox"
                    id="checkbox_financial_management_3rd"
                    className="input"
                    checked={isThirdPartyChecked}
                    onChange={() => setIsThirdPartyChecked((prev) => !prev)}
                  />
                  <label
                    htmlFor="checkbox_financial_management_3rd"
                    className="label"
                  >
                    [{t('T30308')}] {t('T40153')}
                  </label>
                </div>

                <button
                  type="button"
                  className="btn btn_ic"
                  onClick={() => openThirdPartyTermsDetail()}
                >
                  <span className="ic ic_arrow_forward_16px">
                    <span className="blind">{t('T10063')}</span>
                  </span>
                </button>
              </div>
            )}
            {isMobilityConsumptionVisible && !isMobilityConsumptionAgree && (
              <div className="consent_agree_item">
                <div className="input_box">
                  <input
                    type="checkbox"
                    id="checkbox_mobility_consumption"
                    className="input"
                    checked={isMobilityConsumptionChecked}
                    onChange={() => setIsMobilityConsumptionChecked((prev) => !prev)}
                  />
                  <label
                    htmlFor="checkbox_mobility_consumption"
                    className="label"
                  >
                    [{t('T30309')}] {t('T40239')}
                  </label>
                </div>

                <button
                  type="button"
                  className="btn btn_ic"
                  onClick={() => openMobilityConsumptionTermsDetail()}
                >
                  <span className="ic ic_arrow_forward_16px">
                    <span className="blind">{t('T10063')}</span>
                  </span>
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
      <div className="floating_action_area">
        <div className="button_box_complex">
          <button
            type="button"
            className="btn btn_large btn_filled btn_full"
            disabled={requiredConsentCount !== selectedConsentCount}
            onClick={() => {
              emitEventLog({
                event: 'di_vehicle_finance_terms',
                data_action: isFinanceManageAgree ? 'start_reconsent' : 'agree_and_connect',
              });
              agreeFinanceManagement();
            }}
          >
            {isFinanceManageAgree ? t('T40161') : t('T40162')}
          </button>
        </div>
      </div>
    </>
  );
}

export default CapitalTerms;
